import * as api from "@/api";
import LocalStorage from "@/classes/local-storage";
import { getPresetSearchEngines } from "@/data/preset-search-engines";
import * as signals from "@/signals";
import { SearchEngineInfo } from "@/types";
import { useSignalEffect } from "@preact/signals";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";

function useSearchEngines(): {
	searchEngines: SearchEngineInfo[];
} {
	const [region, setRegion] = useState<string | null>(null);

	const { data } = useQuery({
		initialData: () => ({
			results: LocalStorage.getCustomSearchEngines(),
		}),
		placeholderData: keepPreviousData,
		queryFn: () => {
			if (!signals.login.value.username) {
				return null;
			}
			return api.getSearchEngines();
		},
		queryKey: ["searchEngines", signals.login.value.username],
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		retry: false,
	});

	const searchEngines = useMemo(() => {
		const nameToIcon = {
			[t("baidu")]: "\uE651",
			[t("bing")]: "\uE608",
			[t("google")]: "\uE624",
			[t("sogou")]: "\uECA8",
			[t("360Search")]: "\uE602",
			DuckDuckGo: "\uEB82",
			Yandex: "\uE600",
		};
		const presets = getPresetSearchEngines(region);
		const customSearchEngines = data?.results ?? [];
		const results = [...presets, ...customSearchEngines];
		return results.map((engine) => ({
			...engine,
			icon: nameToIcon[engine.name] || "\uE70D",
		}));
	}, [data?.results, region]);

	useEffect(() => {
		const selectedSearchEngineId = LocalStorage.getSelectedSearchEngineId();
		if (selectedSearchEngineId) {
			const searchEngine = searchEngines.find(
				(engine) => engine.id === selectedSearchEngineId,
			);
			if (searchEngine) {
				signals.selectedSearchEngine.value = searchEngine;
			}
		}
	}, [searchEngines]);

	useSignalEffect(() => {
		setRegion(signals.dynamicInfo.value.region || null);
	});

	return { searchEngines };
}

export default useSearchEngines;
